import { useState, useEffect, useCallback } from 'react';
import { requestPhoneOtp, verifyPhoneOtp } from '../services/authService';

export const OTP_LENGTH = 6;
export const OTP_TIMEOUT_SEC = 3 * 60;

/**
 * 전화번호 OTP 인증 훅 — 발송/검증/타이머/상태 캡슐화
 *
 * @param {object} params
 * @param {string} params.phoneDigits - 숫자만 남긴 전화번호 (외부 컴포넌트에서 입력/포맷 관리)
 * @param {string} [params.purpose] - CF purpose ("signup" 기본)
 *
 * 반환값:
 *   sent, code[], codeString, remainSec, loading, errMsg,
 *   verifiedToken, verified (현재 phoneDigits와 인증된 번호가 일치할 때 true),
 *   sendOtp(), verify(), setCodeAt(idx, val), handleCodeKey(idx, e), handleCodePaste(e), reset()
 */
export const usePhoneOtp = ({ phoneDigits = '', purpose = 'signup' } = {}) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''));
  const [remainSec, setRemainSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [verifiedToken, setVerifiedToken] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');

  useEffect(() => {
    if (!sent || remainSec <= 0) return undefined;
    const t = setInterval(() => setRemainSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [sent, remainSec]);

  // 전화번호가 바뀌면 인증 상태 무효화
  useEffect(() => {
    if (verifiedToken && verifiedPhone && verifiedPhone !== phoneDigits) {
      setVerifiedToken('');
      setVerifiedPhone('');
    }
  }, [phoneDigits, verifiedPhone, verifiedToken]);

  const codeString = code.join('');
  const verified = !!verifiedToken && verifiedPhone === phoneDigits && phoneDigits.length >= 10;

  const sendOtp = useCallback(async () => {
    if (loading) return;
    if (phoneDigits.length < 10) {
      setErrMsg('전화번호를 정확히 입력해 주세요.');
      return;
    }
    setLoading(true);
    setErrMsg('');
    try {
      await requestPhoneOtp(phoneDigits, purpose);
      setSent(true);
      setCode(Array(OTP_LENGTH).fill(''));
      setRemainSec(OTP_TIMEOUT_SEC);
      setVerifiedToken('');
      setVerifiedPhone('');
    } catch (err) {
      setErrMsg(err?.message || '인증번호 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [loading, phoneDigits, purpose]);

  const verify = useCallback(async () => {
    if (loading || codeString.length !== OTP_LENGTH || remainSec <= 0) return null;
    setLoading(true);
    setErrMsg('');
    try {
      const { verifiedToken: token } = await verifyPhoneOtp(phoneDigits, codeString);
      setVerifiedToken(token);
      setVerifiedPhone(phoneDigits);
      return token;
    } catch (err) {
      setErrMsg(err?.message || '인증에 실패했습니다.');
      setCode(Array(OTP_LENGTH).fill(''));
      return null;
    } finally {
      setLoading(false);
    }
  }, [loading, codeString, remainSec, phoneDigits]);

  // val이 1자리면 해당 칸만 채우고, 여러 자리면 idx부터 우측으로 분배 — 자동완성/SMS one-time-code/복붙 onChange 경유 케이스 대응
  const setCodeAt = useCallback((idx, val, inputsRef) => {
    const digits = (val || '').replace(/\D/g, '');
    if (!digits) {
      setCode((prev) => {
        const next = [...prev];
        next[idx] = '';
        return next;
      });
      setErrMsg('');
      return;
    }
    const fillLen = Math.min(digits.length, OTP_LENGTH - idx);
    setCode((prev) => {
      const next = [...prev];
      for (let i = 0; i < fillLen; i++) next[idx + i] = digits[i];
      return next;
    });
    setErrMsg('');
    const focusIdx = Math.min(idx + fillLen, OTP_LENGTH - 1);
    inputsRef?.current?.[focusIdx]?.focus();
  }, []);

  const handleCodeKey = useCallback((idx, e, inputsRef) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef?.current?.[idx - 1]?.focus();
    }
  }, [code]);

  const handleCodePaste = useCallback((e, inputsRef) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    inputsRef?.current?.[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }, []);

  const reset = useCallback(() => {
    setSent(false);
    setCode(Array(OTP_LENGTH).fill(''));
    setRemainSec(0);
    setErrMsg('');
    setVerifiedToken('');
    setVerifiedPhone('');
  }, []);

  return {
    sent,
    code,
    codeString,
    remainSec,
    loading,
    errMsg,
    verifiedToken,
    verified,
    sendOtp,
    verify,
    setCodeAt,
    handleCodeKey,
    handleCodePaste,
    reset,
    OTP_LENGTH,
  };
};

export const formatOtpTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
